import type { CardProps } from '@mantine/core';
import { Box, Card, Divider, Group, Stack } from '@mantine/core';
import type { ComponentPropsWithoutRef } from 'react';
import Image from 'next/image';

import { Avatar } from '~/domains/design-system/Avatar';
import { Text } from '~/domains/design-system/Text';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import { getCdnUrl } from '~/domains/assets/utils/getCdnUrl';
import dt from '~/testing';

const PADDING = 16;

function ChannelBanner({
  src = getCdnUrl('/images/channels/channel-banner-default.png'),
  children,
}: {
  src?: string;
  children?: React.ReactNode;
}) {
  return (
    <Box
      data-test-id={dt.channel.connectedChannelHeader.channelBanner}
      sx={{
        position: 'relative',
        height: '150px',
        img: {
          objectFit: 'cover',
        },
      }}
    >
      <Image src={src} fill alt="" />
      {children}
    </Box>
  );
}

export function ChannelHeader(props: CardProps) {
  const isMobile = useIsMobile();

  return (
    <Card
      p={0}
      /* eslint-disable-line local-rules/use-theme-tokens */ mb={isMobile ? 0 : 16}
      /* eslint-disable-line local-rules/use-theme-tokens */ radius={isMobile ? 0 : 8}
      shadow={isMobile ? undefined : '0px 8px 24px 0px rgba(0, 0, 0, 0.1)'}
      {...props}
    />
  );
}

function ChannelHeaderContent({ children, ...props }: ComponentPropsWithoutRef<typeof Stack>) {
  return (
    <Stack p={PADDING} /* eslint-disable-line local-rules/use-theme-tokens */ spacing={12} {...props}>
      {children}
    </Stack>
  );
}

function ChannelHeaderHeader({ children }: { children: React.ReactNode }) {
  return <Group position="apart">{children}</Group>;
}

function ChannelAvatar(props: ComponentPropsWithoutRef<typeof Avatar>) {
  return (
    <Avatar size={64} {...props} data-test-id={dt.channel.connectedChannelHeader.channelAvatar} />
  );
}

function ChannelNumbers({ children }: { children: React.ReactNode }) {
  return (
    <Group
      spacing={0}
      noWrap
      sx={{
        a: { padding: 0 },
      }}
    >
      {children}
    </Group>
  );
}

function ChannelNumbersDivider() {
  return (
    <Divider
      orientation="vertical"
      color="gray.1"
      sx={{ alignSelf: 'center' }}
      // TODO: once the typography is set, the height most likely won't be needed
      h={44}
    />
  );
}

function ChannelNumbersItem({
  children,
  ...props
}: {
  children: React.ReactNode;
  'data-test-id'?: string;
}) {
  return (
    <Stack data-test-id={props['data-test-id']} /* eslint-disable-line local-rules/use-theme-tokens */ spacing={2} align="center" miw={88} px={4}>
      {children}
    </Stack>
  );
}

function ChannelNumber({
  children,
  ...props
}: {
  children: React.ReactNode;
  'data-test-id'?: string;
}) {
  return (
    <Text variant="headline-large" color="gray.9" data-test-id={props['data-test-id']}>
      {children}
    </Text>
  );
}

function ChannelNumberLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text variant="body-medium" color="gray.6">
      {children}
    </Text>
  );
}

function ChannelInfo({ children }: { children: React.ReactNode }) {
  return (
    <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} pb={4}>
      {children}
    </Stack>
  );
}

function ChannelName({
  children,
  endAdornment,
}: {
  children: React.ReactNode;
  endAdornment?: React.ReactNode;
}) {
  return (
    <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4}>
      <Text
        data-test-id={dt.channel.connectedChannelHeader.channelName}
        variant="headline-xl"
        color="gray.9"
      >
        {children}
      </Text>
      {endAdornment}
    </Group>
  );
}

function ChannelFollowsCurrentUserTag({ children }: { children: React.ReactNode }) {
  return (
    <Text
      variant="subhead-x-small"
      color="gray.7"
      sx={(theme) => ({
        borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ '16px',
        padding: /* eslint-disable-line local-rules/use-theme-tokens */ '1.5px 6px 2.5px 6px',
        background: theme.colors.gray[1],
      })}
    >
      {children}
    </Text>
  );
}

// TODO: move it somewhere else, perhaps an icon folder
function ChannelVerifiedBadge() {
  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      width={16}
      height={30}
      fill="none"
      // TODO: verify it once designs are settled (currently there is a different gap between the badge and follows tag)
      /* eslint-disable-line local-rules/use-theme-tokens */ ml="2px"
    >
      <circle cx={8} cy={16} r={6.87} fill="#101113" />
      <path
        fill="#4BEBE2"
        d="m6.899 19.727 5.543-5.543-1.36-1.37L6.9 16.995 4.853 14.95l-1.36 1.371L6.9 19.727ZM8 24a7.81 7.81 0 0 1-3.128-.629 8.066 8.066 0 0 1-2.536-1.707A8.066 8.066 0 0 1 .63 19.128 7.809 7.809 0 0 1 0 16c0-1.114.21-2.156.629-3.128a8.066 8.066 0 0 1 1.707-2.536A8.066 8.066 0 0 1 4.872 8.63 7.81 7.81 0 0 1 8 8a7.81 7.81 0 0 1 3.128.629c.972.42 1.817.988 2.536 1.707a8.067 8.067 0 0 1 1.707 2.536A7.81 7.81 0 0 1 16 16a7.81 7.81 0 0 1-.629 3.128 8.067 8.067 0 0 1-1.707 2.536 8.067 8.067 0 0 1-2.536 1.707A7.81 7.81 0 0 1 8 24Z"
      />
    </Box>
  );
}

function ChannelHandle({ children }: { children: React.ReactNode }) {
  return (
    <Text
      data-test-id={dt.channel.connectedChannelHeader.channelHandle}
      variant="body-large"
      color="gray.6"
    >
      @{children}
    </Text>
  );
}

function ChannelDescription({ children }: { children: React.ReactNode }) {
  return (
    <Text
      data-test-id={dt.channel.connectedChannelHeader.channelDescription}
      variant="body-large"
      color="gray.9"
      sx={{
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
      }}
    >
      {children}
    </Text>
  );
}

function ChannelActions({ children }: { children: React.ReactNode }) {
  return (
    <Group
      /* eslint-disable-line local-rules/use-theme-tokens */ spacing={12}
      grow
      sx={{
        'a,button': {
          flex: 1,
        },
      }}
    >
      {children}
    </Group>
  );
}

ChannelHeader.Banner = ChannelBanner;
ChannelHeader.Content = ChannelHeaderContent;

ChannelHeader.Header = ChannelHeaderHeader;
ChannelHeader.Avatar = ChannelAvatar;

ChannelHeader.Numbers = ChannelNumbers;
ChannelHeader.NumbersItem = ChannelNumbersItem;
ChannelHeader.NumbersDivider = ChannelNumbersDivider;
ChannelHeader.Number = ChannelNumber;
ChannelHeader.NumberLabel = ChannelNumberLabel;

ChannelHeader.Info = ChannelInfo;
ChannelHeader.Name = ChannelName;
ChannelHeader.FollowsCurrentUserTag = ChannelFollowsCurrentUserTag;
ChannelHeader.VerifiedBadge = ChannelVerifiedBadge;
ChannelHeader.Handle = ChannelHandle;
ChannelHeader.Description = ChannelDescription;

ChannelHeader.Actions = ChannelActions;
